import type { FC} from 'react';
import { useContext, useEffect, useState } from 'react';
import type { DataLayerEvent} from '@magpie/ui-core';
import { DataLayerService, Divider, Skeleton } from '@magpie/ui-core';
import { useApi, useClientSettings, useExpressWidgetError, useWidgetSettings } from '../../hooks';
import styles from './WidgetContent.module.scss';
import { WidgetContext } from '../../context/WidgetContextProvider/WidgetContextProvider';
import ApplePayExpressButton from '../ApplePayExpressButton/ApplePayExpressButton';
import KlarnaExpressCheckoutButton from '../KlarnaExpressCheckoutButton/KlarnaExpressCheckoutButton';
import PaypalExpressButton from '../PaypalExpressButton/PaypalExpressButton';
import getIsApplePayAvailable from '../../utils/applePay/getIsApplePayAvailable';
import { forterScript, forterUpdatedSdkScript } from '../../utils/forter-script';
import { getIsAnyButtonReady, handleComponentError, setForterToken } from '../../utils';
import ErrorToast from '../ErrorToast/ErrorToast';
import { HandleGetActiveBasketSummary } from '../../utils/handleGetActiveBasketSummary';
import StockAllocationError from '../../errors/StockAllocationError';
import WINDOW_LOCATION from '../../constants/windowLocation';
import { removeParam } from '../../utils/window-location/removeParam';

const attachForterScript = (siteId: string, useUpdatedForterJsSdk: boolean) => {
    const script = document.createElement('script');
    const finalScript = useUpdatedForterJsSdk ? forterUpdatedSdkScript : forterScript;
    finalScript.replace('forterSiteId', siteId);
    script.id = siteId;
    script.type = 'text/javascript';
    script.innerHTML = finalScript;
    document.body.appendChild(script);
};

export const WidgetContent: FC = () => {
    const [isApplePayAvailable, setIsApplePayAvailable] = useState<boolean | null>(null);
    const clientSettings = useClientSettings();
    const { showAsAlternative, basketTotal } = useWidgetSettings();
    const api = useApi();
    const { setExpressWidgetError } = useExpressWidgetError();
    const [state, dispatch] = useContext(WidgetContext);
    const expressCheckoutOptions = clientSettings.expressCheckoutOptions;
    const {
        isKlarnaButtonDisplayed,
        isPaypalButtonDisplayed,
        isApplePayButtonDisplayed,
        isAnyButtonReady,
        isKlarnaButtonAvailable,
    } = state;

    const expressPayPalOrKlarnaEnabled = () => {
        return [
            expressCheckoutOptions?.klarnaExpressCheckoutEnabled,
            expressCheckoutOptions?.paypalExpressCheckoutEnabled,
        ].some(Boolean);
    };

    const showDivider = () => {
        if (!showAsAlternative) return false;

        return isKlarnaButtonDisplayed || isPaypalButtonDisplayed || (isApplePayAvailable && isApplePayButtonDisplayed);
    };

    const pushActiveBasketReferenceEvent = (orderNumber: string) => {
        DataLayerService.addEvent<DataLayerEvent>({
            event: 'analyticsEvent',
            eventCategory: 'ecommerce',
            eventAction: 'active-basket-reference',
            eventLabel: orderNumber,
            eventNonInteraction: false,
        });
    };

    useEffect(() => {
        const handleTokenReady = (event: Event) => {
            setForterToken((event as CustomEvent<string>).detail);
        };

        if (clientSettings.enableForterTokenTriggerIntegration && clientSettings.forterSiteId) {
            document.addEventListener('ftr:tokenReady', handleTokenReady);
            attachForterScript(clientSettings.forterSiteId, clientSettings.useUpdatedForterJsSdk);
        }

        // Remove event listener on hook destroy
         
        return () => {
            if (clientSettings.enableForterTokenTriggerIntegration) {
                document.removeEventListener('ftr:tokenReady', handleTokenReady);
            }
        };
    }, [clientSettings]);

    useEffect(() => {
        const url = new URL(window.location.href);
        if (url.searchParams.get(WINDOW_LOCATION.SHOW_OUT_OF_STOCK) === String(true)) {
            handleComponentError(new StockAllocationError(''), setExpressWidgetError);
            removeParam(WINDOW_LOCATION.SHOW_OUT_OF_STOCK);
        }
    }, []);

    useEffect(() => {
        const nextIsAnyButtonReady = getIsAnyButtonReady(
            expressCheckoutOptions,
            {
                isKlarnaButtonAvailable,
                isApplePayAvailable,
            },
            { isApplePayButtonDisplayed, isKlarnaButtonDisplayed, isPaypalButtonDisplayed },
        );

        dispatch({ type: 'SET_IS_ANY_BUTTON_READY', payload: nextIsAnyButtonReady });
    }, [
        expressCheckoutOptions,
        isKlarnaButtonDisplayed,
        isPaypalButtonDisplayed,
        isApplePayButtonDisplayed,
        isKlarnaButtonAvailable,
    ]);

    useEffect(() => {
        const initializePaymentProvidersData = async () => {
            dispatch({ type: 'SET_ACTIVE_BASKET_SUMMARY', payload: null });
            const activeBasketSummary = await HandleGetActiveBasketSummary(api, setExpressWidgetError);
            if (activeBasketSummary) {
                pushActiveBasketReferenceEvent(activeBasketSummary.number);
                dispatch({ type: 'SET_ACTIVE_BASKET_SUMMARY', payload: activeBasketSummary });

                const applePayAvailable = await getIsApplePayAvailable(api, activeBasketSummary);
                setIsApplePayAvailable(applePayAvailable);
            }
        };

        void initializePaymentProvidersData();
    }, [basketTotal]);

    return (
        <>
            <ErrorToast />
            <div className={styles.expressButtonsContainer}>
                {showDivider() ? (
                    <Divider color="midgrey">
                        <h6>OR</h6>
                    </Divider>
                ) : null}
                {!isAnyButtonReady && (
                    <div data-testid="mainSkeletonTestId">
                        <Skeleton color="#d7d7d7" height="55px" rounded="sm" width="100%" />
                    </div>
                )}
                {expressPayPalOrKlarnaEnabled() ? (
                    <>
                        {clientSettings.expressCheckoutOptions?.klarnaExpressCheckoutEnabled && (
                            <KlarnaExpressCheckoutButton />
                        )}
                        {clientSettings.expressCheckoutOptions?.paypalExpressCheckoutEnabled && (
                            <PaypalExpressButton clientSettings={clientSettings} />
                        )}
                    </>
                ) : null}
                {expressCheckoutOptions?.applePayExpressCheckoutEnabled && isApplePayAvailable ? (
                    <ApplePayExpressButton />
                ) : null}
            </div>
        </>
    );
};
